import { useCallback, useEffect } from 'react';
import { FontAsset } from '../stores/types/font-assets';

interface Props {
	fonts: FontAsset[];
}

// Module-level cache to track loaded fonts across all hook instances
const loadedFonts = new Set<string>();

/**
 * Dynamically injects @font-face rules for custom fonts and provides
 * className generation for applying those fonts to elements.
 *
 * Fonts are loaded once globally and persist across component lifecycles.
 *
 * @param fonts - Array of FontAsset objects with url and name properties
 * @returns Object with getFontClassName helper to generate consistent class names
 */
const useDynamicFonts = ({ fonts }: Props) => {
	useEffect(() => {
		// Only inject fonts that haven't been loaded yet
		const newFonts = fonts.filter(font => !loadedFonts.has(font.url));

		if (newFonts.length === 0) return;

		const styleId = 'dynamic-font-faces';
		let styleTag = document.getElementById(styleId) as HTMLStyleElement;

		if (!styleTag) {
			styleTag = document.createElement('style');
			styleTag.id = styleId;
			document.head.appendChild(styleTag);
		}

		// Generate @font-face rules and corresponding CSS classes for new fonts only
		const fontFaceRules = newFonts.map((font) => {
			const sanitizedName = sanitizeFontUrl(font.url);
			const className = `font-${sanitizedName}`;

			// Mark this font as loaded
			loadedFonts.add(font.url);

			return `
				@font-face {
				font-family: "custom-${sanitizedName}";
				src: url("${font.url}");
				}

				.${className} {
				font-family: "custom-${sanitizedName}", sans-serif;
			}`;
		}).join('\n');

		// Append new rules to existing content instead of replacing
		styleTag.textContent += fontFaceRules;
	}, [fonts]);
};

/**
 * Creates a sanitized identifier from a font URL
 * Extracts filename and removes special characters
 */
function sanitizeFontUrl(url: string): string {
	// Extract filename from URL (everything after last /)
	const filename = url.split('/').pop() || url;

	// Remove file extension and special characters, keep alphanumeric and hyphens
	return filename
		.replace(/\.(ttf|otf|woff2?)/gi, '')
		.replace(/[^a-zA-Z0-9-]/g, '-')
		.toLowerCase();
}

export function getFontClassName(font: FontAsset): string {
	const sanitizedName = sanitizeFontUrl(font.url);
	return `font-${sanitizedName}`;
}

export default useDynamicFonts;
